import React, { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Calendar, Download, FileText, MapPin, MessageCircle, Plus, Printer, Search, Trash2, Users, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import LoadingSpinner from '@/components/ui/LoadingSpinner';
import EmptyState from '@/components/ui/EmptyState';
import {
  getRegistrationAvailability,
  getPublicRegistrationPageBySlug,
  listPublicRegistrationPages,
  lookupPublicRegistrationReceipt,
  submitPublicRegistration,
  uploadPublicAuthorizationTerm,
} from '@/api/eventRegistrationService';
import { toast } from 'sonner';

const emptyParticipant = (relationship = 'Responsavel') => ({
  full_name: '',
  cpf: '',
  phone: '',
  email: '',
  birth_date: '',
  gender: '',
  address: '',
  relationship,
  is_minor: false,
  guardian_name: '',
  guardian_phone: '',
});

const paymentMethods = ['Dinheiro', 'Pix', 'Cartao', 'Boleto', 'Rifa'];

function formatDate(date) {
  if (!date) return null;
  const [year, month, day] = String(date).slice(0, 10).split('-');
  if (!year || !month || !day) return date;
  return `${day}/${month}/${year}`;
}

function formatTime(time) {
  if (!time) return null;
  return String(time).slice(0, 5);
}

function formatEventPeriod(page) {
  const startDate = page.start_date || page.event_date;
  const endDate = page.end_date;
  const startLabel = formatDate(startDate);
  const endLabel = endDate && endDate !== startDate ? formatDate(endDate) : null;
  const startTime = formatTime(page.start_time);
  const endTime = formatTime(page.end_time);

  if (!startLabel) return null;
  const dateText = endLabel ? `${startLabel} a ${endLabel}` : startLabel;
  const timeText = startTime && endTime ? `${startTime} às ${endTime}` : startTime;
  return timeText ? `${dateText} · ${timeText}` : dateText;
}

function formatPrice(value) {
  if (value == null || value === '') return null;
  return Number(value).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function getParticipantAmount(page, participant) {
  if (!page) return 0;
  const age = calculateAge(participant.birth_date);
  const rules = Array.isArray(page.age_price_rules) ? page.age_price_rules : [];
  const matchedRule = rules.find((rule) => (
    age !== null &&
    age >= Number(rule.min_age || 0) &&
    age <= Number(rule.max_age || 0)
  ));
  if (matchedRule) return Number(matchedRule.price || 0);
  if (!participant.is_minor && page.adult_price != null && page.adult_price !== '') return Number(page.adult_price) || 0;
  return Number(page.price || 0);
}

function calculateAge(birthDate) {
  if (!birthDate) return null;
  const birth = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  return age;
}

function getAllowedPaymentInstallments(page, referenceDate = new Date()) {
  const eventDateValue = page?.start_date || page?.event_date;
  if (!eventDateValue) return 1;

  const eventDate = new Date(`${String(eventDateValue).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(eventDate.getTime())) return 1;

  const monthDifference = (
    (eventDate.getFullYear() - referenceDate.getFullYear()) * 12
  ) + eventDate.getMonth() - referenceDate.getMonth();

  return Math.max(monthDifference + 1, 1);
}

function getYesNoAnswerValue(value) {
  if (value && typeof value === 'object' && !Array.isArray(value)) return value;
  if (value === true) return { answer: 'Sim', reason: '' };
  if (value === false) return { answer: 'Nao', reason: '' };
  return { answer: '', reason: '' };
}

function getQuestionOptions(question) {
  if (question?.type === 'custom_options') {
    return [
      String(question.option_a_label || '').trim() || 'Opção 1',
      String(question.option_b_label || '').trim() || 'Opção 2',
    ];
  }
  return ['Sim', 'Nao'];
}

function generateRegistrationCode() {
  const randomPart = Math.random().toString(36).slice(2, 6).toUpperCase();
  return `MBV-${Date.now().toString().slice(-6)}-${randomPart}`;
}

function normalizeCpf(value) {
  return String(value || '').replace(/\D/g, '');
}

function getDiscountAmount(registration) {
  const total = Number(registration?.total_amount || 0);
  return Math.min(Math.max(Number(registration?.discount_amount || 0), 0), total);
}

function getNetAmount(registration) {
  return Math.max(Number(registration?.total_amount || 0) - getDiscountAmount(registration), 0);
}

function hasMinorParticipant(registration) {
  return (Array.isArray(registration?.participants) ? registration.participants : []).some((participant) => participant.is_minor);
}

function buildReceiptHtml(registration, page) {
  const participants = Array.isArray(registration?.participants) ? registration.participants : [];
  const createdAt = registration?.created_date ? new Date(registration.created_date).toLocaleString('pt-BR') : '';
  return `
    <html>
      <head>
        <title>Comprovante de inscrição</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111827; padding: 24px; }
          h1 { font-size: 24px; margin: 0 0 4px; }
          h2 { font-size: 16px; margin-top: 22px; border-bottom: 1px solid #ddd; padding-bottom: 6px; }
          p { margin: 4px 0; font-size: 13px; }
          .muted { color: #6b7280; }
          .box { border: 1px solid #ddd; padding: 12px; margin-top: 10px; border-radius: 8px; }
          .code { font-size: 22px; font-weight: 700; letter-spacing: .04em; color: #047857; }
          .screen-actions { display: flex; gap: 8px; margin-top: 24px; }
          .screen-actions button { border: 1px solid #d1d5db; background: #fff; border-radius: 8px; padding: 10px 14px; cursor: pointer; }
          @media print { .screen-actions { display: none; } }
        </style>
      </head>
      <body>
        <p class="muted">Comprovante de inscrição</p>
        <h1>${page?.title || 'Evento'}</h1>
        <p>${formatEventPeriod(page) || ''}${page?.location ? ` · ${page.location}` : ''}</p>
        <div class="box">
          <p>Código da inscrição</p>
          <p class="code">${registration?.registration_code || ''}</p>
          <p><strong>Responsável:</strong> ${registration?.contact_name || ''}</p>
          <p><strong>Telefone:</strong> ${registration?.contact_phone || ''}</p>
          <p><strong>Data da inscrição:</strong> ${createdAt}</p>
          <p><strong>Status:</strong> ${registration?.registration_status || ''}</p>
          <p><strong>Pagamento:</strong> ${registration?.payment_status || ''} · ${registration?.payment_method || 'Forma não informada'}</p>
        </div>
        <h2>Participantes</h2>
        ${participants.map((participant, index) => `
          <div class="box">
            <p><strong>${index + 1}. ${participant.full_name || ''}</strong></p>
            <p>${participant.relationship || ''} · ${participant.gender || ''} · ${participant.birth_date ? formatDate(participant.birth_date) : 'Sem nascimento'}</p>
            <p>CPF: ${participant.cpf || 'Não informado'} · Telefone: ${participant.phone || 'Não informado'}</p>
            ${participant.is_minor ? `<p><strong>Responsável menor:</strong> ${participant.guardian_name || ''} · ${participant.guardian_phone || ''}</p>` : ''}
            <p>Valor: ${formatPrice(participant.amount || 0) || 'Gratuito'}</p>
          </div>
        `).join('')}
        <h2>Valores</h2>
        <p><strong>Valor original:</strong> ${formatPrice(registration?.total_amount || 0) || 'Gratuito'}</p>
        ${getDiscountAmount(registration) > 0 ? `<p><strong>Desconto:</strong> ${formatPrice(getDiscountAmount(registration))}</p>` : ''}
        <p><strong>Valor final:</strong> ${formatPrice(getNetAmount(registration)) || 'Gratuito'}</p>
        <h2>Orientações</h2>
        <p>${page?.payment_instructions || 'Pagamento conforme orientação da equipe do evento.'}</p>
        ${hasMinorParticipant(registration) ? '<p>Esta inscrição possui menor de idade. O termo de autorização do responsável deve ser entregue conforme orientação da equipe.</p>' : ''}
        <div class="screen-actions">
          <button type="button" onclick="window.print()">Imprimir novamente</button>
          <button type="button" onclick="window.close()">Voltar</button>
        </div>
        <script>
          let printed = false;
          window.addEventListener('afterprint', () => {
            printed = true;
            setTimeout(() => window.close(), 300);
          });
          window.addEventListener('pagehide', () => {
            if (printed) window.close();
          });
        </script>
      </body>
    </html>
  `;
}

function printReceipt(registration, page) {
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(buildReceiptHtml(registration, page));
  printWindow.document.close();
  printWindow.focus();
  printWindow.addEventListener('load', () => {
    setTimeout(() => printWindow.print(), 250);
  });
}

function buildTermsHtml(page) {
  return `
    <html>
      <head>
        <title>Termo de regulamento</title>
        <style>
          body { font-family: Arial, sans-serif; color: #111827; padding: 24px; line-height: 1.5; }
          h1 { font-size: 22px; margin-bottom: 4px; }
          p { font-size: 14px; white-space: pre-wrap; }
          a { color: #047857; }
          .screen-actions { display: flex; gap: 8px; margin-top: 24px; }
          .screen-actions button { border: 1px solid #d1d5db; background: #fff; border-radius: 8px; padding: 10px 14px; cursor: pointer; }
          @media print { .screen-actions { display: none; } }
        </style>
      </head>
      <body>
        <p>Termo de regulamento</p>
        <h1>${page?.title || 'Evento'}</h1>
        <p>${page?.terms_text || 'Termo disponível no arquivo informado pela organização.'}</p>
        ${page?.terms_url ? `<p><strong>Arquivo:</strong> <a href="${page.terms_url}" target="_blank" rel="noreferrer">${page.terms_url}</a></p>` : ''}
        <div class="screen-actions">
          <button type="button" onclick="window.print()">Imprimir</button>
          <button type="button" onclick="window.close()">Voltar</button>
        </div>
      </body>
    </html>
  `;
}

function openTerms(page) {
  if (page?.terms_url) {
    window.open(page.terms_url, '_blank', 'noopener,noreferrer');
    return;
  }
  printTerms(page, false);
}

function printTerms(page, shouldPrint = true) {
  if (shouldPrint && page?.terms_url && !page?.terms_text) {
    window.open(page.terms_url, '_blank', 'noopener,noreferrer');
    return;
  }
  const printWindow = window.open('', '_blank');
  if (!printWindow) return;
  printWindow.document.write(buildTermsHtml(page));
  printWindow.document.close();
  printWindow.focus();
  if (shouldPrint) {
    printWindow.addEventListener('load', () => {
      setTimeout(() => printWindow.print(), 250);
    });
  }
}

function shareReceiptOnWhatsApp(registration, page) {
  const url = `${window.location.origin}${window.location.pathname}?codigo=${encodeURIComponent(registration?.registration_code || '')}`;
  const message = encodeURIComponent([
    `Comprovante de inscrição - ${page?.title || 'Evento'}`,
    `Código: ${registration?.registration_code || ''}`,
    `Responsável: ${registration?.contact_name || ''}`,
    `Valor final: ${formatPrice(getNetAmount(registration)) || 'Gratuito'}`,
    `Acessar novamente: ${url}`,
  ].join('\n'));
  window.open(`https://wa.me/?text=${message}`, '_blank', 'noopener,noreferrer');
}

function ParticipantFields({ participant, index, isPrimary, requireCpf, requireAddress, onChange, onRemove }) {
  const title = isPrimary ? 'Responsável pela inscrição' : `Participante ${index + 1}`;

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="font-semibold text-gray-900">{title}</h3>
        {!isPrimary && (
          <Button type="button" size="sm" variant="outline" className="text-red-600 border-red-200" onClick={onRemove}>
            <Trash2 className="w-4 h-4" />
          </Button>
        )}
      </div>

      <div>
        <Label>Nome completo *</Label>
        <Input value={participant.full_name} onChange={(event) => onChange('full_name', event.target.value)} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>CPF {requireCpf ? '*' : ''}</Label>
          <Input value={participant.cpf} onChange={(event) => onChange('cpf', event.target.value)} placeholder="000.000.000-00" />
        </div>
        <div>
          <Label>Data de nascimento *</Label>
          <Input type="date" value={participant.birth_date} onChange={(event) => onChange('birth_date', event.target.value)} />
          {participant.birth_date && (
            <p className="text-xs text-gray-500 mt-1">
              {participant.is_minor ? 'Menor de idade' : 'Maior de idade'} · {calculateAge(participant.birth_date)} anos
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>Sexo *</Label>
          <Select value={participant.gender} onValueChange={(value) => onChange('gender', value)}>
            <SelectTrigger>
              <SelectValue placeholder="Selecione" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Feminino">Feminino</SelectItem>
              <SelectItem value="Masculino">Masculino</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Telefone {isPrimary ? '*' : ''}</Label>
          <Input value={participant.phone} onChange={(event) => onChange('phone', event.target.value)} placeholder="(00) 00000-0000" />
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <Label>E-mail</Label>
          <Input type="email" value={participant.email} onChange={(event) => onChange('email', event.target.value)} />
        </div>
      </div>

      <div>
        <Label>Endereço {requireAddress ? '*' : ''}</Label>
        <Input value={participant.address} onChange={(event) => onChange('address', event.target.value)} />
      </div>

      {!isPrimary && (
        <div>
          <Label>Vínculo familiar</Label>
          <Select value={participant.relationship} onValueChange={(value) => onChange('relationship', value)}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="Conjuge">Cônjuge</SelectItem>
              <SelectItem value="Filho">Filho(a)</SelectItem>
              <SelectItem value="Familiar">Familiar</SelectItem>
              <SelectItem value="Outro">Outro</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}

      {participant.is_minor && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg bg-amber-50 border border-amber-100 p-3">
          <div>
            <Label>Nome do responsável *</Label>
            <Input value={participant.guardian_name} onChange={(event) => onChange('guardian_name', event.target.value)} />
          </div>
          <div>
            <Label>Telefone do responsável *</Label>
            <Input value={participant.guardian_phone} onChange={(event) => onChange('guardian_phone', event.target.value)} />
          </div>
        </div>
      )}
    </div>
  );
}

function RegistrationReceipt({ registration, page, onNewRegistration, onRegistrationUpdated }) {
  const participants = Array.isArray(registration?.participants) ? registration.participants : [];
  const needsAuthorization = hasMinorParticipant(registration);
  const createdAt = registration?.created_date ? new Date(registration.created_date).toLocaleString('pt-BR') : null;
  const authorizationUploadedAt = registration?.authorization_uploaded_at
    ? new Date(registration.authorization_uploaded_at).toLocaleString('pt-BR')
    : null;

  const authorizationUploadMutation = useMutation({
    mutationFn: (file) => uploadPublicAuthorizationTerm({
      registrationId: registration?.id,
      registrationCode: registration?.registration_code,
      file,
    }),
    onSuccess: (updated) => {
      toast.success('Termo assinado enviado com sucesso.');
      onRegistrationUpdated?.({ ...registration, ...updated, page });
    },
    onError: (error) => toast.error(error?.message || 'Erro ao enviar termo assinado.'),
  });

  const handleAuthorizationUpload = (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    authorizationUploadMutation.mutate(file);
  };

  return (
    <div className="max-w-2xl mx-auto bg-white rounded-xl border border-gray-200 p-5">
      <div className="text-center">
        <CheckCircle2 className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
        <p className="text-sm font-medium text-emerald-700">Comprovante de inscrição</p>
        <h1 className="text-2xl font-bold text-gray-900 mt-1">{page?.title || 'Evento'}</h1>
        {formatEventPeriod(page) && <p className="text-sm text-gray-600 mt-1">{formatEventPeriod(page)}</p>}
      </div>

      {registration?.is_waitlist && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 p-3 text-center">
          <p className="font-semibold text-amber-900">Lista de espera</p>
          <p className="text-sm text-amber-800">Sua inscrição foi registrada, mas ainda não garante vaga.</p>
        </div>
      )}

      <div className="mt-4 rounded-lg bg-emerald-50 border border-emerald-100 p-3 text-center">
        <p className="text-sm text-emerald-800">Código da inscrição</p>
        <p className="text-2xl font-bold text-emerald-900 tracking-wide">{registration?.registration_code}</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 text-sm">
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500">Responsável</p>
          <p className="font-semibold text-gray-900">{registration?.contact_name}</p>
          <p className="text-gray-600">{registration?.contact_phone || 'Sem telefone'}</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500">Status</p>
          <p className="font-semibold text-gray-900">{registration?.registration_status || 'Recebida'}</p>
          <p className="text-gray-600">Pagamento: {registration?.payment_status || 'Pendente'} · {registration?.payment_method || 'Forma não informada'}</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500">Inscrição</p>
          <p className="font-semibold text-gray-900">{createdAt || 'Data não registrada'}</p>
          <p className="text-gray-600">{participants.length} participante(s)</p>
        </div>
        <div className="rounded-lg bg-gray-50 p-3">
          <p className="text-gray-500">Valor final</p>
          <p className="font-semibold text-gray-900">{formatPrice(getNetAmount(registration)) || 'Gratuito'}</p>
          {getDiscountAmount(registration) > 0 && <p className="text-rose-700">Desconto: {formatPrice(getDiscountAmount(registration))}</p>}
        </div>
      </div>

      <div className="mt-4">
        <p className="font-semibold text-gray-900 mb-2">Participantes</p>
        <div className="space-y-2">
          {participants.map((participant, index) => (
            <div key={`${participant.full_name}-${index}`} className="rounded-lg border border-gray-200 p-3 text-sm">
              <p className="font-medium text-gray-900">{participant.full_name}</p>
              <p className="text-gray-600">{participant.relationship || 'Participante'} · {participant.birth_date ? formatDate(participant.birth_date) : 'Sem nascimento'}</p>
              {participant.is_minor && <p className="text-amber-700">Menor: {participant.guardian_name} · {participant.guardian_phone}</p>}
            </div>
          ))}
        </div>
      </div>

      {needsAuthorization && page?.authorization_term_url && (
        <div className="mt-4 rounded-lg bg-amber-50 border border-amber-100 p-3">
          <p className="font-semibold text-amber-900">Termo de autorização do responsável</p>
          <p className="text-sm text-amber-800 mt-1">Baixe o termo, assine e envie a imagem aqui caso a equipe precise validar.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Button className="bg-white" variant="outline" onClick={() => window.open(page.authorization_term_url, '_blank', 'noopener,noreferrer')}>
              <FileText className="w-4 h-4 mr-2" />
              Abrir termo de autorização
            </Button>
            {registration?.authorization_file_url && (
              <Button className="bg-white" variant="outline" onClick={() => window.open(registration.authorization_file_url, '_blank', 'noopener,noreferrer')}>
                <FileText className="w-4 h-4 mr-2" />
                Ver termo enviado
              </Button>
            )}
          </div>
          <div className="mt-3">
            <Label>Enviar imagem do termo assinado</Label>
            <Input type="file" accept="image/*" onChange={handleAuthorizationUpload} disabled={authorizationUploadMutation.isPending} />
            <p className="text-xs text-amber-800 mt-1">
              {authorizationUploadMutation.isPending
                ? 'Enviando termo assinado...'
                : authorizationUploadedAt
                  ? `Termo enviado em ${authorizationUploadedAt}.`
                  : 'Nenhum termo assinado enviado ainda.'}
            </p>
          </div>
        </div>
      )}

      {(page?.terms_text || page?.terms_url) && (
        <div className="mt-4 rounded-lg bg-gray-50 border border-gray-200 p-3">
          <p className="font-semibold text-gray-900">Termo de regulamento</p>
          <p className="text-sm text-gray-600 mt-1">Consulte ou imprima as regras e orientações da inscrição.</p>
          <div className="flex flex-col sm:flex-row gap-2 mt-3">
            <Button className="bg-white" variant="outline" onClick={() => openTerms(page)}>
              <FileText className="w-4 h-4 mr-2" />
              Ver regulamento
            </Button>
            <Button className="bg-white" variant="outline" onClick={() => printTerms(page)}>
              <Printer className="w-4 h-4 mr-2" />
              Imprimir regulamento
            </Button>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-5">
        <Button variant="outline" onClick={() => printReceipt(registration, page)}>
          <Printer className="w-4 h-4 mr-2" />
          Imprimir
        </Button>
        <Button variant="outline" onClick={() => printReceipt(registration, page)}>
          <Download className="w-4 h-4 mr-2" />
          Salvar PDF
        </Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => shareReceiptOnWhatsApp(registration, page)}>
          <MessageCircle className="w-4 h-4 mr-2" />
          WhatsApp
        </Button>
      </div>

      <p className="text-xs text-gray-500 mt-4 text-center">
        Para acessar novamente, guarde o código e o telefone do responsável.
      </p>

      {onNewRegistration && (
        <Button className="mt-4 w-full" variant="ghost" onClick={onNewRegistration}>
          Fazer outra inscrição
        </Button>
      )}
    </div>
  );
}

function ReceiptLookup({ initialCode, onFound }) {
  const [registrationCode, setRegistrationCode] = useState(initialCode || '');
  const [phone, setPhone] = useState('');
  const [cpf, setCpf] = useState('');
  const [matches, setMatches] = useState([]);

  const lookupMutation = useMutation({
    mutationFn: () => lookupPublicRegistrationReceipt({ registrationCode, phone, cpf }),
    onSuccess: (receipts) => {
      const results = Array.isArray(receipts) ? receipts : receipts ? [receipts] : [];
      if (results.length === 0) {
        setMatches([]);
        toast.error('Comprovante não encontrado. Confira os dados informados.');
        return;
      }
      if (results.length === 1) {
        onFound(results[0]);
        return;
      }
      setMatches(results);
      toast.success('Comprovante localizado.');
    },
    onError: (error) => toast.error(error?.message || 'Erro ao localizar comprovante.'),
  });

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!registrationCode && !phone && !cpf) {
      toast.error('Informe o código, telefone ou CPF.');
      return;
    }
    lookupMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="max-w-lg mx-auto bg-white rounded-xl border border-gray-200 p-5 space-y-4">
      <div className="text-center">
        <Search className="w-10 h-10 text-emerald-600 mx-auto mb-2" />
        <h1 className="text-2xl font-bold text-gray-900">Acessar comprovante</h1>
        <p className="text-sm text-gray-600 mt-1">Use o código da inscrição, telefone ou CPF.</p>
      </div>
      <div>
        <Label>Código da inscrição</Label>
        <Input value={registrationCode} onChange={(event) => setRegistrationCode(event.target.value)} placeholder="MBV-000000-ABCD" />
      </div>
      <div>
        <Label>Telefone do responsável</Label>
        <Input value={phone} onChange={(event) => setPhone(event.target.value)} placeholder="(00) 00000-0000" />
      </div>
      <div>
        <Label>CPF do responsável ou participante</Label>
        <Input value={cpf} onChange={(event) => setCpf(event.target.value)} placeholder="000.000.000-00" />
      </div>
      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={lookupMutation.isPending}>
        {lookupMutation.isPending ? 'Buscando...' : 'Acessar comprovante'}
      </Button>
      {matches.length > 1 && (
        <div className="border-t pt-4 space-y-2">
          <p className="text-sm font-semibold text-gray-900">Escolha uma inscrição</p>
          {matches.map((receipt) => (
            <button
              type="button"
              key={receipt.id}
              className="w-full text-left rounded-lg border border-gray-200 p-3 hover:bg-gray-50"
              onClick={() => onFound(receipt)}
            >
              <p className="font-semibold text-gray-900">{receipt.page?.title || 'Evento'}</p>
              <p className="text-sm text-gray-600">{receipt.registration_code} · {receipt.contact_name}</p>
              <p className="text-xs text-gray-500">{receipt.created_date ? new Date(receipt.created_date).toLocaleString('pt-BR') : 'Data não registrada'}</p>
            </button>
          ))}
        </div>
      )}
    </form>
  );
}

export default function InscricaoEvento() {
  const params = new URLSearchParams(typeof window !== 'undefined' ? window.location.search : '');
  const slug = params.get('evento');
  const receiptCode = params.get('codigo') || '';
  const receiptMode = Boolean(receiptCode || params.get('comprovante'));
  const [selectedPageId, setSelectedPageId] = useState('');
  const [participants, setParticipants] = useState([emptyParticipant()]);
  const [paymentMethod, setPaymentMethod] = useState('');
  const [paymentInstallments, setPaymentInstallments] = useState('1');
  const [authorizationDelivery, setAuthorizationDelivery] = useState('Entregar em maos');
  const [authorizationFile, setAuthorizationFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [answers, setAnswers] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsOpenedAt, setTermsOpenedAt] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [submittedRegistration, setSubmittedRegistration] = useState(null);
  const [recoveredReceipt, setRecoveredReceipt] = useState(null);

  const { data: pageBySlug, isLoading: loadingSlug } = useQuery({
    queryKey: ['publicRegistrationPage', slug],
    queryFn: () => getPublicRegistrationPageBySlug(slug),
    enabled: !!slug,
  });

  const { data: pages = [], isLoading: loadingPages } = useQuery({
    queryKey: ['publicRegistrationPages'],
    queryFn: () => listPublicRegistrationPages(),
    enabled: !slug,
  });

  const selectedPage = useMemo(() => {
    if (slug) return pageBySlug;
    return pages.find((page) => page.id === selectedPageId) || pages[0];
  }, [pageBySlug, pages, selectedPageId, slug]);

  const hasMinor = participants.some((participant) => participant.is_minor);

  const { data: availability } = useQuery({
    queryKey: ['registrationAvailability', selectedPage?.id],
    queryFn: () => getRegistrationAvailability(selectedPage?.id),
    enabled: !!selectedPage?.id,
  });

  const takenSpots = availability?.taken_spots || 0;
  const availableSpots = selectedPage?.max_spots ? Math.max(Number(selectedPage.max_spots) - takenSpots, 0) : null;
  const isWaitlist = availableSpots !== null && participants.length > availableSpots && selectedPage?.allow_waitlist;
  const customQuestions = Array.isArray(selectedPage?.custom_questions) ? selectedPage.custom_questions : [];
  const maxPaymentInstallments = getAllowedPaymentInstallments(selectedPage);
  const paymentInstallmentOptions = Array.from({ length: maxPaymentInstallments }, (_, index) => String(index + 1));
  const pricedParticipants = participants.map((participant) => ({
    ...participant,
    amount: getParticipantAmount(selectedPage, participant),
  }));
  const totalAmount = pricedParticipants.reduce((sum, participant) => sum + Number(participant.amount || 0), 0);
  const isPaymentExempt = totalAmount <= 0;

  useEffect(() => {
    if (isPaymentExempt && paymentMethod !== 'Isento') {
      setPaymentMethod('Isento');
      setPaymentInstallments('1');
    }
    if (!isPaymentExempt && paymentMethod === 'Isento') {
      setPaymentMethod('');
    }
  }, [isPaymentExempt, paymentMethod]);

  useEffect(() => {
    if (Number(paymentInstallments || 1) > maxPaymentInstallments) {
      setPaymentInstallments(String(maxPaymentInstallments));
    }
  }, [maxPaymentInstallments, paymentInstallments]);

  const submitMutation = useMutation({
    mutationFn: () => submitPublicRegistration({
      page_id: selectedPage.id,
      registration_code: generateRegistrationCode(),
      participants: pricedParticipants,
      answers,
      total_amount: totalAmount,
      is_waitlist: isWaitlist,
      payment_method: isPaymentExempt ? 'Isento' : paymentMethod,
      payment_installments: paymentInstallments,
      authorization_delivery: authorizationDelivery,
      authorization_file: authorizationFile,
      notes,
      accepted_terms: acceptedTerms,
      terms_opened_at: termsOpenedAt,
    }),
    onSuccess: (registration) => {
      setSubmittedRegistration(registration);
      setSubmitted(true);
      toast.success('Inscrição enviada com sucesso!');
    },
    onError: (error) => {
      toast.error(error?.message || 'Erro ao enviar inscrição.');
    },
  });

  const updateParticipant = (index, field, value) => {
    setParticipants((current) => current.map((participant, itemIndex) => (
      itemIndex === index ? { ...participant, [field]: value } : participant
    )));
  };

  const validate = () => {
    if (!selectedPage?.id) return 'Selecione uma inscrição aberta.';
    if (availableSpots !== null && participants.length > availableSpots && !selectedPage.allow_waitlist) return `Restam apenas ${availableSpots} vaga(s) para esta inscrição.`;
    if (!isPaymentExempt && !paymentMethod) return 'Escolha a forma de pagamento presencial.';
    if (!isPaymentExempt && Number(paymentInstallments || 1) > maxPaymentInstallments) return `Esta inscrição aceita no máximo ${maxPaymentInstallments} parcela(s), conforme a data do evento.`;
    if (selectedPage.terms_url && !termsOpenedAt) return 'A inscrição só é aceita depois que você abrir o termo da inscrição.';
    if (!acceptedTerms) return 'Para enviar a inscrição, é obrigatório concordar com os termos.';

    for (const participant of participants) {
      if (!participant.full_name || !participant.birth_date) return 'Nome e data de nascimento são obrigatórios para todos.';
      if (!participant.gender) return 'Sexo é obrigatório para todos os participantes.';
      if (selectedPage.require_cpf && !participant.cpf) return 'CPF é obrigatório para todos os participantes.';
      if (selectedPage.require_address && !participant.address) return 'Endereço é obrigatório para todos os participantes.';
      if (participant.is_minor && (!participant.guardian_name || !participant.guardian_phone)) {
        return 'Menores precisam ter nome e telefone do responsável.';
      }
    }

    const repeatedCpf = participants
      .map((participant) => normalizeCpf(participant.cpf))
      .filter((cpf) => cpf.length >= 11)
      .find((cpf, index, cpfs) => cpfs.indexOf(cpf) !== index);
    if (repeatedCpf) return 'CPF repetido na inscrição. Cada participante precisa ter um CPF diferente.';

    for (const question of customQuestions) {
      if (question.required && ['yes_no', 'custom_options'].includes(question.type)) {
        const value = getYesNoAnswerValue(answers[question.id]);
        if (!value.answer) return `Responda: ${question.label}`;
      }
      if (question.required && question.type === 'checkbox' && answers[question.id] !== true) {
        return `Marque: ${question.label}`;
      }
      if (question.required && !['checkbox', 'yes_no', 'custom_options'].includes(question.type) && !String(answers[question.id] || '').trim()) {
        return `Responda: ${question.label}`;
      }
    }

    if (!participants[0]?.phone) return 'Telefone do responsável pela inscrição é obrigatório.';
    return null;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const error = validate();
    if (error) {
      toast.error(error);
      return;
    }
    submitMutation.mutate();
  };

  const updateParticipantField = (index, field, value) => {
    if (field === 'birth_date') {
      const age = calculateAge(value);
      updateParticipant(index, 'birth_date', value);
      updateParticipant(index, 'is_minor', age !== null && age < 18);
      return;
    }
    updateParticipant(index, field, value);
  };

  const handleOpenTerms = () => {
    if (!selectedPage.terms_url) return;
    setTermsOpenedAt((current) => current || new Date().toISOString());
    window.open(selectedPage.terms_url, '_blank', 'noopener,noreferrer');
  };

  if (loadingSlug || loadingPages) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <LoadingSpinner />
      </div>
    );
  }

  if (recoveredReceipt) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 py-8">
        <RegistrationReceipt
          registration={recoveredReceipt}
          page={recoveredReceipt.page}
          onRegistrationUpdated={setRecoveredReceipt}
          onNewRegistration={() => {
            setRecoveredReceipt(null);
            window.location.href = window.location.pathname;
          }}
        />
      </div>
    );
  }

  if (receiptMode) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 py-8">
        <ReceiptLookup initialCode={receiptCode} onFound={setRecoveredReceipt} />
      </div>
    );
  }

  if (!selectedPage) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 flex items-center">
        <div className="max-w-lg mx-auto w-full">
          <EmptyState icon={Calendar} title="Nenhuma inscrição aberta" description="No momento não há eventos com inscrição disponível." />
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 py-8">
        <RegistrationReceipt
          registration={submittedRegistration}
          page={selectedPage}
          onRegistrationUpdated={setSubmittedRegistration}
          onNewRegistration={() => window.location.reload()}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-10">
      <div className="bg-slate-900 text-white px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-emerald-200 font-medium">Inscrição de evento</p>
            <Button
              type="button"
              size="sm"
              variant="secondary"
              className="bg-white/10 text-white hover:bg-white/20"
              onClick={() => window.location.href = `${window.location.pathname}?comprovante=1`}
            >
              <Search className="w-4 h-4 mr-2" />
              Já tenho inscrição
            </Button>
          </div>
          <h1 className="text-3xl font-bold mt-1">{selectedPage.title}</h1>
          {selectedPage.description && <p className="text-slate-200 mt-3">{selectedPage.description}</p>}
          <div className="flex flex-wrap gap-3 mt-4 text-sm text-slate-200">
            {formatEventPeriod(selectedPage) && <span className="inline-flex items-center gap-1"><Calendar className="w-4 h-4" />{formatEventPeriod(selectedPage)}</span>}
            {selectedPage.location && <span className="inline-flex items-center gap-1"><MapPin className="w-4 h-4" />{selectedPage.location}</span>}
            {formatPrice(selectedPage.price) && <span>{formatPrice(selectedPage.price)}</span>}
            {selectedPage.max_spots && <span>{takenSpots} inscrito(s) de {selectedPage.max_spots} vaga(s) · {availableSpots} restante(s)</span>}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {!slug && pages.length > 1 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <Label>Evento</Label>
            <Select value={selectedPage.id} onValueChange={setSelectedPageId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {pages.map((page) => (
                  <SelectItem key={page.id} value={page.id}>{page.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {participants.map((participant, index) => (
          <ParticipantFields
            key={index}
            participant={participant}
            index={index}
            isPrimary={index === 0}
            requireCpf={selectedPage.require_cpf}
            requireAddress={selectedPage.require_address}
            onChange={(field, value) => updateParticipantField(index, field, value)}
            onRemove={() => setParticipants((current) => current.filter((_, itemIndex) => itemIndex !== index))}
          />
        ))}

        {selectedPage.allow_family && (
          <Button type="button" variant="outline" className="w-full" onClick={() => setParticipants((current) => [...current, emptyParticipant('Familiar')])}>
            <Plus className="w-4 h-4 mr-2" />
            Adicionar familiar ou participante
          </Button>
        )}

        {isWaitlist && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-900">
            As vagas disponíveis não comportam todos os participantes desta inscrição. Ao enviar, esta família entra na lista de espera.
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-2">
          <h3 className="font-semibold text-gray-900">Resumo de valores</h3>
          {pricedParticipants.map((participant, index) => (
            <div key={`${participant.full_name}-${index}`} className="flex items-center justify-between text-sm">
              <span className="text-gray-600">{participant.full_name || `Participante ${index + 1}`}</span>
              <span className="font-medium text-gray-900">{formatPrice(participant.amount) || 'Gratuito'}</span>
            </div>
          ))}
          <div className="flex items-center justify-between border-t pt-2 mt-2">
            <span className="font-semibold text-gray-900">Total previsto</span>
            <span className="font-bold text-gray-900">{formatPrice(totalAmount) || 'Gratuito'}</span>
          </div>
        </div>

        {customQuestions.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Informações adicionais</h3>
            {customQuestions.map((question) => (
              <div key={question.id}>
                {['yes_no', 'custom_options'].includes(question.type) ? (
                  <div className="space-y-2">
                    <Label>{question.label}{question.required ? ' *' : ''}</Label>
                    <Select
                      value={getYesNoAnswerValue(answers[question.id]).answer}
                      onValueChange={(value) => {
                        const current = getYesNoAnswerValue(answers[question.id]);
                        setAnswers({ ...answers, [question.id]: { ...current, answer: value } });
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={question.type === 'custom_options' ? 'Selecione uma opção' : 'Selecione Sim ou Não'} />
                      </SelectTrigger>
                      <SelectContent>
                        {getQuestionOptions(question).map((option) => (
                          <SelectItem key={option} value={option}>{option === 'Nao' ? 'Não' : option}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Textarea
                      value={getYesNoAnswerValue(answers[question.id]).reason}
                      onChange={(event) => {
                        const current = getYesNoAnswerValue(answers[question.id]);
                        setAnswers({ ...answers, [question.id]: { ...current, reason: event.target.value } });
                      }}
                      placeholder={question.reason_placeholder || 'Por qual motivo da resposta?'}
                      rows={3}
                    />
                  </div>
                ) : question.type === 'checkbox' ? (
                  <label className="flex items-start gap-2 text-sm text-gray-700">
                    <Checkbox
                      checked={answers[question.id] === true}
                      onCheckedChange={(checked) => setAnswers({ ...answers, [question.id]: Boolean(checked) })}
                    />
                    {question.label}{question.required ? ' *' : ''}
                  </label>
                ) : (
                  <>
                    <Label>{question.label}{question.required ? ' *' : ''}</Label>
                    <Input
                      value={answers[question.id] || ''}
                      onChange={(event) => setAnswers({ ...answers, [question.id]: event.target.value })}
                      placeholder={question.placeholder || ''}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <h3 className="font-semibold text-gray-900">Pagamento presencial</h3>
          {selectedPage.payment_instructions && <p className="text-sm text-gray-600">{selectedPage.payment_instructions}</p>}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Forma de pagamento *</Label>
              <Select value={paymentMethod} onValueChange={setPaymentMethod} disabled={isPaymentExempt}>
                <SelectTrigger>
                  <SelectValue placeholder="Escolha" />
                </SelectTrigger>
                <SelectContent>
                  {isPaymentExempt && <SelectItem value="Isento">Isento</SelectItem>}
                  {paymentMethods.map((method) => (
                    <SelectItem key={method} value={method}>{method === 'Cartao' ? 'Cartão' : method}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Parcelas</Label>
              <Select value={paymentInstallments} onValueChange={setPaymentInstallments} disabled={isPaymentExempt}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {paymentInstallmentOptions.map((installment) => (
                    <SelectItem key={installment} value={installment}>
                      {installment}x
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {hasMinor && (
          <div className="bg-white rounded-xl border border-amber-200 p-4 space-y-3">
            <h3 className="font-semibold text-gray-900">Autorização para menor</h3>
            <p className="text-sm text-gray-600">
              A inscrição garante a vaga, mas ficará com autorização pendente até aprovação pela administração.
            </p>
            {selectedPage.authorization_term_url && (
              <a className="text-sm font-medium text-emerald-700" href={selectedPage.authorization_term_url} target="_blank" rel="noreferrer">
                Baixar termo de autorização
              </a>
            )}
            <Select value={authorizationDelivery} onValueChange={setAuthorizationDelivery}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Entregar em maos">Vou entregar em mãos na igreja</SelectItem>
                <SelectItem value="Enviar foto">Vou enviar foto do termo assinado</SelectItem>
              </SelectContent>
            </Select>
            {authorizationDelivery === 'Enviar foto' && (
              <Input type="file" accept="image/*,.pdf" onChange={(event) => setAuthorizationFile(event.target.files?.[0] || null)} />
            )}
          </div>
        )}

        <div className="bg-white rounded-xl border border-gray-200 p-4 space-y-3">
          <Label>Observações</Label>
          <Textarea value={notes} onChange={(event) => setNotes(event.target.value)} rows={3} placeholder="Alguma informação importante para a equipe?" />
          <div className="rounded-lg bg-gray-50 border border-gray-100 p-3">
            <p className="text-sm text-gray-600 whitespace-pre-line">
              {selectedPage.terms_text || 'Ao enviar esta inscrição, você confirma que as informações são verdadeiras e está ciente das orientações do evento, pagamento presencial e autorização para menores.'}
            </p>
          </div>
          {selectedPage.terms_url && (
            <div className="rounded-lg border border-emerald-100 bg-emerald-50 p-3">
              <Button type="button" variant="outline" className="w-full bg-white" onClick={handleOpenTerms}>
                Abrir termo da inscrição
              </Button>
              <p className="text-xs text-emerald-800 mt-2">
                {termsOpenedAt ? 'Termo aberto. Agora você pode confirmar o aceite.' : 'A inscrição só será aceita se você abrir o termo da inscrição antes de confirmar.'}
              </p>
            </div>
          )}
          <label className="flex items-start gap-2 text-sm text-gray-700">
            <Checkbox
              checked={acceptedTerms}
              disabled={Boolean(selectedPage.terms_url && !termsOpenedAt)}
              onCheckedChange={(checked) => setAcceptedTerms(Boolean(checked))}
            />
            <span>
              Li e concordo com os termos da inscrição. *
              {selectedPage.terms_url && !termsOpenedAt && (
                <span className="block text-xs text-amber-700 mt-1">
                  Para enviar a inscrição, abra o termo da inscrição primeiro.
                </span>
              )}
            </span>
          </label>
        </div>

        <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700" disabled={submitMutation.isPending}>
          <Users className="w-4 h-4 mr-2" />
          {submitMutation.isPending ? 'Enviando...' : 'Garantir inscrição'}
        </Button>
      </form>
    </div>
  );
}
